/**
 * CameraDirector — picks cinematic camera shots based on replay events.
 *
 * Selects dramatic angles, holds shots for appropriate durations,
 * and smoothly transitions between them. Thinks like a TV director
 * cutting a Star Trek episode.
 */

import type { Vec3, SpatialState } from "../../types/game";
import type { ReplayFrame } from "./ReplayEngine";

// ── Public types ──

export interface CameraShot {
  position: Vec3;
  lookAt: Vec3;
  fov: number;
  roll: number;
  transitionSpeed: number; // lerp speed per second (0.5 = slow dramatic, 3 = fast action cut)
}

type ShotType =
  | "establishing"
  | "follow"
  | "over_shoulder"
  | "two_shot"
  | "action_tracking"
  | "impact_closeup"
  | "dramatic_push"
  | "scenic_orbit"
  | "tracking_wide"
  | "slow_pullback"
  | "flyby";

// ── Helpers ──

function vec3Dist(a: Vec3, b: Vec3): number {
  const dx = a.x - b.x, dy = a.y - b.y, dz = a.z - b.z;
  return Math.sqrt(dx * dx + dy * dy + dz * dz);
}

function vec3Add(a: Vec3, b: Vec3): Vec3 {
  return { x: a.x + b.x, y: a.y + b.y, z: a.z + b.z };
}

function vec3Sub(a: Vec3, b: Vec3): Vec3 {
  return { x: a.x - b.x, y: a.y - b.y, z: a.z - b.z };
}

function vec3Scale(v: Vec3, s: number): Vec3 {
  return { x: v.x * s, y: v.y * s, z: v.z * s };
}

function vec3Normalize(v: Vec3): Vec3 {
  const len = Math.sqrt(v.x * v.x + v.y * v.y + v.z * v.z) || 1;
  return { x: v.x / len, y: v.y / len, z: v.z / len };
}

function vec3Cross(a: Vec3, b: Vec3): Vec3 {
  return {
    x: a.y * b.z - a.z * b.y,
    y: a.z * b.x - a.x * b.z,
    z: a.x * b.y - a.y * b.x,
  };
}

function vec3Lerp(a: Vec3, b: Vec3, t: number): Vec3 {
  return {
    x: a.x + (b.x - a.x) * t,
    y: a.y + (b.y - a.y) * t,
    z: a.z + (b.z - a.z) * t,
  };
}

// ── Director ──

export class CameraDirector {
  private currentShot: CameraShot;
  private targetShot: CameraShot;
  private shotType: ShotType = "establishing";
  private shotStartTime = 0;
  private shotMinDuration = 5000; // ms before allowing a cut
  private currentTime = 0;
  private orbitAngle = 0;

  // Track context for shot decisions
  private lastCombatTime = -Infinity;
  private lastDialogueTime = -Infinity;

  constructor(initialSpatial: SpatialState) {
    const pp = initialSpatial.playerShip.position;
    const hd = initialSpatial.playerShip.heading;

    // Start with a wide establishing shot
    const behindOffset = vec3Scale(hd, -80);
    const startPos = vec3Add(pp, { x: behindOffset.x, y: 40, z: behindOffset.z });

    this.currentShot = {
      position: startPos,
      lookAt: pp,
      fov: 50,
      roll: 0,
      transitionSpeed: 0.3,
    };
    this.targetShot = { ...this.currentShot };
  }

  /** Call each frame with the current replay state. Returns interpolated camera. */
  update(frame: ReplayFrame, currentTime: number, deltaMs: number): CameraShot {
    this.currentTime = currentTime;

    // Track combat/dialogue timing
    if (frame.newEffects.length > 0) {
      const combatEffects = frame.newEffects.filter(e =>
        ["beam", "torpedo", "shield_impact", "explosion"].includes(e.effect.effectType)
      );
      if (combatEffects.length > 0) this.lastCombatTime = currentTime;
    }
    if (frame.newDialogue.length > 0) {
      this.lastDialogueTime = currentTime;
    }

    // Decide if we need a new shot
    const timeSinceShot = currentTime - this.shotStartTime;
    const shouldCut = this.shouldCut(frame, timeSinceShot);

    if (shouldCut) {
      this.pickShot(frame);
    }

    // Update orbit angle for scenic shots
    this.orbitAngle += (deltaMs / 1000) * 0.15;

    // Smoothly interpolate current camera toward target
    const lerpT = Math.min(1, this.targetShot.transitionSpeed * (deltaMs / 1000));
    this.currentShot = {
      position: vec3Lerp(this.currentShot.position, this.targetShot.position, lerpT),
      lookAt: vec3Lerp(this.currentShot.lookAt, this.targetShot.lookAt, lerpT),
      fov: this.currentShot.fov + (this.targetShot.fov - this.currentShot.fov) * lerpT,
      roll: this.currentShot.roll + (this.targetShot.roll - this.currentShot.roll) * lerpT,
      transitionSpeed: this.targetShot.transitionSpeed,
    };

    return this.currentShot;
  }

  private shouldCut(frame: ReplayFrame, timeSinceShot: number): boolean {
    // Never cut too early
    if (timeSinceShot < 2000) return false;

    // Force cut on major events
    if (frame.alertLevel === "red" && this.shotType !== "dramatic_push" && timeSinceShot > 1500) return true;
    if (frame.missionEnded && this.shotType !== "slow_pullback") return true;

    // Combat event — cut faster
    if (frame.newEffects.some(e => ["beam", "torpedo"].includes(e.effect.effectType)) && timeSinceShot > 3000) return true;
    if (frame.newEffects.some(e => ["explosion", "shield_impact"].includes(e.effect.effectType)) && timeSinceShot > 2000) return true;

    // New dialogue speaker — cut to them
    if (frame.newDialogue.length > 0 && timeSinceShot > 4000) return true;

    // Regular shot expiry
    if (timeSinceShot > this.shotMinDuration) return true;

    return false;
  }

  private pickShot(frame: ReplayFrame) {
    const spatial = frame.spatial;
    const pp = spatial.playerShip.position;
    const hd = spatial.playerShip.heading;
    const timeSinceCombat = this.currentTime - this.lastCombatTime;
    const timeSinceDialogue = this.currentTime - this.lastDialogueTime;
    const inCombat = timeSinceCombat < 5000;

    // Mission start — establishing shot
    if (this.currentTime < 5000) {
      this.setEstablishingShot(pp, hd);
      return;
    }

    // Mission end — slow pull-back
    if (frame.missionEnded) {
      this.setSlowPullback(pp, hd);
      return;
    }

    // Red alert declared — dramatic push
    if (frame.alertLevel === "red" && this.shotType !== "dramatic_push") {
      this.setDramaticPush(pp, hd);
      return;
    }

    // Weapon impact — close-up on target
    const impactEffect = frame.newEffects.find(e =>
      ["shield_impact", "explosion"].includes(e.effect.effectType)
    );
    if (impactEffect) {
      const impactPos = impactEffect.effect.position || impactEffect.effect.to || pp;
      this.setImpactCloseup(impactPos, pp);
      return;
    }

    // Weapon fire — action tracking
    const fireEffect = frame.newEffects.find(e =>
      ["beam", "torpedo"].includes(e.effect.effectType)
    );
    if (fireEffect && fireEffect.effect.from && fireEffect.effect.to) {
      this.setActionTracking(fireEffect.effect.from, fireEffect.effect.to);
      return;
    }

    // Active dialogue — over-shoulder or two-shot
    if (timeSinceDialogue < 3000 && !inCombat) {
      const npc = this.findNearestNPC(spatial, pp);
      if (npc) {
        // Alternate between over-shoulder and two-shot
        if (this.shotType === "over_shoulder") {
          this.setTwoShot(pp, npc.position);
        } else {
          this.setOverShoulder(pp, hd, npc.position);
        }
        return;
      }
    }

    // Combat but no specific event — tracking wide
    if (inCombat) {
      this.setTrackingWide(pp, hd);
      return;
    }

    // Ship moving — follow or parallel tracking
    if (spatial.playerShip.speed !== "all_stop") {
      // Alternate between follow and flyby
      if (this.shotType === "follow") {
        this.setFlyby(pp, hd);
      } else {
        this.setFollow(pp, hd);
      }
      return;
    }

    // Orbiting something — scenic orbit
    if (spatial.playerShip.orbitTarget) {
      const target = spatial.entities.find(e => e.id === spatial.playerShip.orbitTarget);
      if (target) {
        this.setScenicOrbit(pp, target.position);
        return;
      }
    }

    // Default — slow flyby
    this.setFlyby(pp, hd);
  }

  // ── Shot setters ──

  private setEstablishingShot(pp: Vec3, hd: Vec3) {
    this.shotType = "establishing";
    this.shotStartTime = this.currentTime;
    this.shotMinDuration = 6000;

    const behind = vec3Scale(hd, -60);
    this.targetShot = {
      position: vec3Add(pp, { x: behind.x, y: 35, z: behind.z }),
      lookAt: vec3Add(pp, vec3Scale(hd, 20)),
      fov: 45,
      roll: 0,
      transitionSpeed: 0.4,
    };
  }

  private setFollow(pp: Vec3, hd: Vec3) {
    this.shotType = "follow";
    this.shotStartTime = this.currentTime;
    this.shotMinDuration = 6000;

    const behind = vec3Scale(hd, -12);
    this.targetShot = {
      position: vec3Add(pp, { x: behind.x, y: 3, z: behind.z }),
      lookAt: vec3Add(pp, vec3Scale(hd, 50)),
      fov: 60,
      roll: 0,
      transitionSpeed: 1.5,
    };
  }

  private setOverShoulder(pp: Vec3, _hd: Vec3, npcPos: Vec3) {
    this.shotType = "over_shoulder";
    this.shotStartTime = this.currentTime;
    this.shotMinDuration = 5000;

    // Camera behind and slightly to the side of the player ship
    const toNpc = vec3Normalize(vec3Sub(npcPos, pp));
    const right = vec3Normalize(vec3Cross(toNpc, { x: 0, y: 1, z: 0 }));
    const behind = vec3Scale(toNpc, -15);

    this.targetShot = {
      position: vec3Add(pp, vec3Add(behind, vec3Add(vec3Scale(right, 8), { x: 0, y: 4, z: 0 }))),
      lookAt: npcPos,
      fov: 50,
      roll: 0,
      transitionSpeed: 1.2,
    };
  }

  private setTwoShot(pp: Vec3, npcPos: Vec3) {
    this.shotType = "two_shot";
    this.shotStartTime = this.currentTime;
    this.shotMinDuration = 6000;

    // Camera perpendicular to the line between ships, offset up and to the side
    const midpoint = vec3Lerp(pp, npcPos, 0.5);
    const toNpc = vec3Normalize(vec3Sub(npcPos, pp));
    const right = vec3Normalize(vec3Cross(toNpc, { x: 0, y: 1, z: 0 }));
    const dist = vec3Dist(pp, npcPos);
    const cameraOffset = Math.max(20, dist * 0.6);

    this.targetShot = {
      position: vec3Add(midpoint, vec3Add(vec3Scale(right, cameraOffset), { x: 0, y: 8, z: 0 })),
      lookAt: midpoint,
      fov: 55,
      roll: 0,
      transitionSpeed: 1.0,
    };
  }

  private setActionTracking(from: Vec3, to: Vec3) {
    this.shotType = "action_tracking";
    this.shotStartTime = this.currentTime;
    this.shotMinDuration = 3000;

    // Camera to the side of the line of fire, slightly behind source
    const dir = vec3Normalize(vec3Sub(to, from));
    const right = vec3Normalize(vec3Cross(dir, { x: 0, y: 1, z: 0 }));
    const cameraPos = vec3Add(from, vec3Add(
      vec3Scale(right, 15),
      vec3Add(vec3Scale(dir, -5), { x: 0, y: 5, z: 0 })
    ));

    this.targetShot = {
      position: cameraPos,
      lookAt: vec3Lerp(from, to, 0.6),
      fov: 55,
      roll: 0,
      transitionSpeed: 2.5,
    };
  }

  private setImpactCloseup(impactPos: Vec3, pp: Vec3) {
    this.shotType = "impact_closeup";
    this.shotStartTime = this.currentTime;
    this.shotMinDuration = 2500;

    // Camera close to the impact, slightly above
    const toPlayer = vec3Normalize(vec3Sub(pp, impactPos));
    const cameraPos = vec3Add(impactPos, vec3Add(
      vec3Scale(toPlayer, 12),
      { x: 0, y: 5, z: 0 }
    ));

    this.targetShot = {
      position: cameraPos,
      lookAt: impactPos,
      fov: 45,
      roll: Math.random() * 0.1 - 0.05, // subtle dutch angle for drama
      transitionSpeed: 3.0,
    };
  }

  private setDramaticPush(pp: Vec3, hd: Vec3) {
    this.shotType = "dramatic_push";
    this.shotStartTime = this.currentTime;
    this.shotMinDuration = 4000;

    // Fast zoom toward player ship from forward angle
    const front = vec3Scale(hd, 25);
    this.targetShot = {
      position: vec3Add(pp, vec3Add(front, { x: 0, y: 3, z: 0 })),
      lookAt: pp,
      fov: 40,
      roll: 0,
      transitionSpeed: 2.0,
    };
  }

  private setScenicOrbit(pp: Vec3, targetPos: Vec3) {
    this.shotType = "scenic_orbit";
    this.shotStartTime = this.currentTime;
    this.shotMinDuration = 8000;

    const dist = vec3Dist(pp, targetPos);
    const orbitRadius = dist * 0.8;
    const camPos: Vec3 = {
      x: targetPos.x + Math.cos(this.orbitAngle) * orbitRadius,
      y: targetPos.y + 15,
      z: targetPos.z + Math.sin(this.orbitAngle) * orbitRadius,
    };

    this.targetShot = {
      position: camPos,
      lookAt: vec3Lerp(targetPos, pp, 0.3),
      fov: 50,
      roll: 0,
      transitionSpeed: 0.6,
    };
  }

  private setTrackingWide(pp: Vec3, hd: Vec3) {
    this.shotType = "tracking_wide";
    this.shotStartTime = this.currentTime;
    this.shotMinDuration = 4000;

    const right = vec3Normalize(vec3Cross(hd, { x: 0, y: 1, z: 0 }));
    this.targetShot = {
      position: vec3Add(pp, vec3Add(vec3Scale(right, 30), { x: 0, y: 20, z: 0 })),
      lookAt: pp,
      fov: 55,
      roll: 0,
      transitionSpeed: 1.5,
    };
  }

  private setFlyby(pp: Vec3, hd: Vec3) {
    this.shotType = "flyby";
    this.shotStartTime = this.currentTime;
    this.shotMinDuration = 7000;

    // Camera alongside, slightly ahead, drifting
    const right = vec3Normalize(vec3Cross(hd, { x: 0, y: 1, z: 0 }));
    const side = Math.random() > 0.5 ? 1 : -1;

    this.targetShot = {
      position: vec3Add(pp, vec3Add(
        vec3Scale(right, 18 * side),
        vec3Add(vec3Scale(hd, 8), { x: 0, y: 2 + Math.random() * 4, z: 0 })
      )),
      lookAt: vec3Add(pp, vec3Scale(hd, 5)),
      fov: 55,
      roll: 0.02 * side,
      transitionSpeed: 0.5,
    };
  }

  private setSlowPullback(pp: Vec3, hd: Vec3) {
    this.shotType = "slow_pullback";
    this.shotStartTime = this.currentTime;
    this.shotMinDuration = 10000;

    const behind = vec3Scale(hd, -80);
    this.targetShot = {
      position: vec3Add(pp, { x: behind.x, y: 50, z: behind.z }),
      lookAt: pp,
      fov: 40,
      roll: 0,
      transitionSpeed: 0.25,
    };
  }

  // ── Helpers ──

  private findNearestNPC(spatial: SpatialState, pp: Vec3) {
    let nearest = null;
    let nearestDist = Infinity;
    for (const e of spatial.entities) {
      if (e.type !== "ship") continue;
      const d = vec3Dist(pp, e.position);
      if (d < nearestDist) {
        nearestDist = d;
        nearest = e;
      }
    }
    return nearest;
  }
}
